import {observer} from 'mobx-react-lite'
import React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Hub} from 'yougile-model'
import {LinkButtonNew} from '@components/common/base/buttons'
import {Icon} from '@components/common/base/icon'
import {DoneButton} from '@components/task-card/done-button'
import {
  useTimelineConfig,
  useTimelineNewStore
} from '@components/timeline-new/timeline-new-store'
import {ColumnId} from '@components/timeline-new/types'
import {EditableTitle} from '@components/upper-panel/editable-title'
import {
  useLoadedLazyTaskEffect,
  useSyncEventUpdateEffect
} from '@containers/hooks'
import {isMirroredHub} from '@extensions/mirrored-tasks/is-mirrored-hub'
import {Task} from '@extensions/task'
import {Chevron} from './chevron'
import {TwCell} from './containers'
import {EntityTitle} from './entity-title'
import {NameProps} from './types'

const TwEntityTitle = tw(EntityTitle)`
  text-sm-regular
  text-primary
  truncate
  flex-1
  py-12
`

const TwEditableTitle = tw(EditableTitle)`
  text-sm-regular
  text-primary
  placeholder:text-placeholder-new
  h-16
  min-h-16
  max-h-16
  flex-1
`

export const TwSelected = tw.div`
  absolute
  bg-action-default
  -top-1
  -bottom-1
  -left-2
  w-4
  left-0
`

export const TaskTitle = (props: NameProps) => {
  if (props.hub) {
    return <ExistingTaskTitle {...props} hub={props.hub} />
  } else {
    return <NewTaskTitle {...props} />
  }
}

const ExistingTaskTitle = observer((props: NameProps & {hub: Hub}) => {
  const {hub, level, path, expandable, type} = props
  const [state, store] = useTimelineNewStore()
  const config = useTimelineConfig()
  const editing = store.isCellEditing(path, ColumnId.title)
  const mirroredHub = isMirroredHub(hub)

  useLoadedLazyTaskEffect(hub, 'gantt-table-title-cell')

  let padding = level * 16
  let margin = 0
  if (!expandable) {
    padding += 16
  } else {
    padding -= 8
  }

  padding = Math.max(padding, 0)

  if (expandable && level === -1) {
    margin = -24
  }

  const selected = state.openedHubId === hub.id

  const mirrors = hub.data?.mirrors
  const hubIds = mirrors ? [...mirrors] : [hub.id]

  useSyncEventUpdateEffect('current', {
    filter: hubIds,
    debounceTime: 50,
    deps: hubIds
  })

  const title = Task.getTitle(hub?.id)

  return (
    <TwCell
      style={{paddingLeft: `${padding}px`, marginLeft: margin}}
      className="truncate"
    >
      {selected && <TwSelected />}
      {expandable && <Chevron path={path} type={type} />}
      <DoneButton hub={hub} fromWhere="timeline" isStatic={config.static} />
      {mirroredHub && (
        <div
          className="min-h-16 min-w-16 relative"
          style={{display: 'inline-block'}}
        >
          <Icon
            className="absolute top-1 left-0"
            icon="IconCustomMirroredTask"
          />
        </div>
      )}
      {editing ? (
        <TwEditableTitle
          title={title}
          placeholder={Lang.msg.timeline.taskTitlePlaceholder}
          onSubmit={title => store.editEntityTitle(title, hub.id)}
        />
      ) : (
        <TwEntityTitle hubId={hub.id} path={path} title={title} />
      )}
    </TwCell>
  )
})

const NewTaskTitle = observer((props: NameProps) => {
  const {path, level, expandable, parentId} = props
  const [, store] = useTimelineNewStore()
  const editing = store.isCellEditing(path, ColumnId.title)

  let padding = level * 16
  if (!expandable) {
    padding += 16
  } else {
    padding -= 8
  }

  padding = Math.max(padding, 0)

  if (editing) {
    // todo try not blinking on submit
    return (
      <TwCell style={{paddingLeft: `${padding}px`}} className="truncate">
        <TwEditableTitle
          title=""
          placeholder={Lang.msg.timeline.taskTitlePlaceholder}
          onSubmit={title => store.addColumnTask(title, parentId, path)}
        />
      </TwCell>
    )
  } else {
    return (
      <TwCell style={{paddingLeft: `${padding}px`}} className="truncate">
        <LinkButtonNew
          icon="IconPlus"
          title={Lang.msg.timeline.addTaskBtn}
          color="secondary"
          className="gap-8 h-full"
          onClick={() => store.startEditing(undefined, path, ColumnId.title)}
        />
      </TwCell>
    )
  }
})
